import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { useSaisieLigneMutation } from '../../services/notesExamensApi';
import { useSemestreCloture } from '../../hooks/useSemestreCloture';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';

interface SaisieLigneExamensProps {
  matiereId: number;
  anneeAcademique: string;
  semestre: 'S1' | 'S2';
  classe: string;
  estRattrapage: boolean;
  onSuccess?: () => void;
}

interface SaisieResult {
  matricule: string;
  note: string | null;
  status: 'success' | 'error';
  message: string;
}

const SaisieLigneExamens: React.FC<SaisieLigneExamensProps> = ({
  matiereId,
  anneeAcademique,
  semestre,
  classe,
  estRattrapage,
  onSuccess,
}) => {
  const [saisieLigne, { isLoading: isSaving }] = useSaisieLigneMutation();

  const [matricule, setMatricule] = useState('');
  const [noteExamen, setNoteExamen] = useState('');
  const [absent, setAbsent] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [historique, setHistorique] = useState<SaisieResult[]>([]);

  // Fix 2026-08-28 : détection semestre clôturé.
  // Fix 2026-10-15 : les verrous ne s'appliquent qu'à l'année active.
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const estAnneeActive = !anneeActive || anneeAcademique === anneeActive.libelle;
  const semestreCloture = useSemestreCloture(semestre, estRattrapage, estAnneeActive);

  // Réinitialiser le formulaire pour la prochaine saisie
  const resetForm = () => {
    setMatricule('');
    setNoteExamen('');
    setAbsent(false);
    setMessage(null);
  };

  // Soumettre le formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Fix 2026-08-28 : blocage si semestre clôturé
    if (semestreCloture) {
      setMessage({ type: 'error', text: `🔒 ${semestreCloture.label} — enregistrement refusé` });
      return;
    }

    if (!matricule.trim()) {
      setMessage({ type: 'error', text: 'Veuillez entrer un matricule' });
      return;
    }

    const noteValue = noteExamen === '' ? null : parseFloat(noteExamen);
    if (noteValue !== null && (noteValue < 0 || noteValue > 20)) {
      setMessage({ type: 'error', text: 'La note doit être comprise entre 0 et 20' });
      return;
    }

    try {
      const result = await saisieLigne({
        matricule: matricule.trim().toUpperCase(),
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre: semestre,
        note_examen: noteValue,
        absent: absent,
        est_rattrapage: estRattrapage,
      }).unwrap();

      if (result.success) {
        // Ajouter à l'historique
        const newEntry: SaisieResult = {
          matricule: matricule.trim().toUpperCase(),
          note: noteValue !== null ? noteValue.toString() : null,
          status: 'success',
          message: result.created ? 'Créée' : 'Mise à jour',
        };
        setHistorique(prev => [newEntry, ...prev].slice(0, 20)); // Garder les 20 dernières

        setMessage({
          type: 'success',
          text: `Note ${result.created ? 'créée' : 'mise à jour'} pour ${matricule}`,
        });
        resetForm();
        onSuccess?.();
      }
    } catch (error: any) {
      const errorMsg = error.data?.error || error.data?.message || 'Erreur lors de l\'enregistrement';

      // Ajouter à l'historique avec erreur
      const errorEntry: SaisieResult = {
        matricule: matricule.trim().toUpperCase(),
        note: noteExamen || null,
        status: 'error',
        message: errorMsg,
      };
      setHistorique(prev => [errorEntry, ...prev].slice(0, 20));

      setMessage({
        type: 'error',
        text: errorMsg,
      });
    }
  };

  // Gérer le changement d'absence
  React.useEffect(() => {
    if (absent) {
      setNoteExamen('0');
    }
  }, [absent]);

  return (
    <div className="space-y-6">
      {/* Formulaire de saisie */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
          Saisie Ligne par Ligne - Notes d'Examens
          {estRattrapage && <span className="ml-2 text-orange-600 dark:text-orange-400 text-sm">(Session Rattrapage)</span>}
        </h2>

        {/* Bandeau SEMESTRE CLÔTURÉ (fix 2026-08-28) */}
        {semestreCloture && (
          <div className="mb-4 rounded-lg border-2 border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 flex items-center gap-3">
            <Lock size={22} className="text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-bold text-red-800 dark:text-red-200 text-sm uppercase tracking-wide">
                🔒 Saisie verrouillée — {semestreCloture.label}
              </div>
              <div className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                Vos modifications seront <strong>refusées</strong> à l'enregistrement.
              </div>
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Matricule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Matricule <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={matricule}
                onChange={(e) => setMatricule(e.target.value.toUpperCase())}
                placeholder="Ex: 2024L1001"
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            {/* Note Examen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Note Examen (0-20)
              </label>
              <input
                type="number"
                step="0.25"
                min="0"
                max="20"
                value={noteExamen}
                onChange={(e) => setNoteExamen(e.target.value)}
                disabled={absent}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            {/* Absent */}
            <div className="flex items-end">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={absent}
                  onChange={(e) => setAbsent(e.target.checked)}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Absent</span>
              </label>
            </div>

            {/* Bouton soumettre */}
            <div className="flex items-end">
              <button
                type="submit"
                disabled={!matricule.trim() || isSaving || !!semestreCloture}
                title={semestreCloture ? `🔒 ${semestreCloture.label} — enregistrement refusé` : ''}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {semestreCloture ? '🔒 Verrouillé' : (isSaving ? 'Enregistrement...' : 'Enregistrer')}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Historique des saisies */}
      {historique.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Historique des saisies ({historique.length})
            </h3>
            <button
              onClick={() => setHistorique([])}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Effacer l'historique
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Matricule
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Note
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Statut
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {historique.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.matricule}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {item.note !== null ? `${item.note}/20` : '-'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        item.status === 'success'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      }`}>
                        {item.status === 'success' ? 'Succès' : 'Erreur'}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {item.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Instructions</h4>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <li>1. Entrez le matricule de l'étudiant</li>
          <li>2. Saisissez la note d'examen (entre 0 et 20)</li>
          <li>3. Cochez "Absent" si l'étudiant était absent (note = 0)</li>
          <li>4. Appuyez sur "Enregistrer" ou Entrée pour valider</li>
          <li>5. Le formulaire se réinitialise automatiquement pour la saisie suivante</li>
        </ul>
      </div>
    </div>
  );
};

export default SaisieLigneExamens;
